/**
 * Layer A — relays the theme's native `@shopify/events` bus (already dispatched by
 * page-view-event.js, view-event-elements.js, product-form.js, media-gallery.js,
 * facets.js, cart-drawer.js, cart-discount.js, predictive-search.js, etc.) into a
 * canonical event vocabulary, wrapped with a common-params block.
 *
 * This layer NEVER pushes to window.dataLayer and never calls a GA4 gtag directly.
 * Standard ecommerce events reach GA4 through Shopify's own "Google & YouTube" sales
 * channel app (server-side Pixel) — publishing the same events here again would double
 * count them. This layer only feeds window.Shopify.analytics.publish (for an internal
 * BI/EventHub listener, if one is ever wired up) and a small in-memory ring buffer
 * (window.__ofAnalyticsEvents) used for local QA/diagnostics.
 *
 * See project-context/tracking-plan-master-v1.md section 1-2 for which events live in
 * this layer vs. Layer B (assets/analytics-bridge.js).
 *
 * IMPORTANT: these events extend the native `Event` class, not `CustomEvent` — the
 * payload is assigned directly onto the event object itself (`event.page`, `event.product`,
 * ...), there is no `event.detail`. Verified 2026-07-17 against the real
 * cdn.shopify.com/storefront/standard-events.js source (`Object.assign(this, payload)`
 * in its base event class) after finding this layer silently produced zero events live —
 * every handler originally destructured from `event.detail`, which is always undefined
 * here, so every handler threw immediately and dispatch() never ran.
 *
 * SECOND FIX (2026-07-17, same day): `dispatch()` below only calls
 * `window.Shopify.analytics.publish()` — a real Shopify API, confirmed working, but it's a
 * publish/subscribe call that nothing currently subscribes to (the site's one Custom Pixel
 * only subscribes to checkout_* topics). So every event routed through the local `dispatch()`
 * only reaches the local diagnostic log, not GA4 or anywhere else — for `page_view`/`view_item`/
 * `add_to_cart`/`remove_from_cart`/`view_cart` that's fine (Shopify's Google & YouTube channel
 * sends those independently), but `select_item`/`view_item_list`/`filter_item_list`/`search`
 * have no other path to GA4 at all. Those four now go through `dispatchToDataLayer()` (imported
 * from Layer B) instead, which actually reaches GTM.
 */
import { StandardEvents } from '@shopify/events';
import { commonParams, logEvent, publishToShopify, isDesignMode } from '@theme/analytics-common';
import { dispatch as dispatchToDataLayer } from '@theme/analytics-bridge';

/** Cart-line id -> last known quantity. Lets the diff-free `update` action (which only
 *  carries the new target quantity, not a delta) still resolve to add_to_cart vs
 *  remove_from_cart. Seeded from CartViewEvent so a quantity change right after opening
 *  the cart already has a correct baseline, not just after this session's own edits. */
const cartLineQuantities = new Map();

function dispatch(eventName, params = {}) {
  // Theme-editor edits shouldn't produce analytics noise.
  if (isDesignMode()) return;

  const payload = { ...commonParams(), ...params };
  logEvent('A', eventName, payload);
  publishToShopify(eventName, payload);
}

function itemFromProduct(product, variant) {
  return {
    item_id: variant?.id ?? product.id,
    item_name: product.title,
    item_variant: variant?.title,
    price: variant?.price?.amount,
    currency: variant?.price?.currencyCode,
  };
}

document.addEventListener(StandardEvents.pageView, (event) => {
  const { page } = event;
  dispatch('page_view', { page_location: page.url, page_title: page.title });
});

document.addEventListener(StandardEvents.productView, (event) => {
  const { context, product } = event;
  dispatch('view_item', {
    items: [itemFromProduct(product, product.selectedVariant)],
    // 'page' | 'search' | 'collection' | 'dialog' | 'recommendation' — a view_item with
    // view_context=collection already tells you this visit came from a collection-page
    // click, which covers most of what a separate select_item click event would have
    // told you, without needing to instrument the product-card click itself.
    view_context: context,
  });
});

document.addEventListener(StandardEvents.productSelect, async (event) => {
  const { product, selectedOptions, promise } = event;
  const result = await promise.catch(() => null);
  const variant = result?.variant;
  const item = itemFromProduct(product, variant);
  // Matches the GTM container's pre-existing `select_item` tag, which reads a real GA4
  // ecommerce object (dataLayer.ecommerce) — not the flat `items` shape Layer A otherwise
  // uses for its own diagnostic log.
  dispatchToDataLayer('select_item', {
    ecommerce: { currency: item.currency, value: item.price, items: [item] },
    content_id: item.item_id,
    selected_options: selectedOptions,
  });
});

document.addEventListener(StandardEvents.collectionView, (event) => {
  const { collection } = event;
  dispatchToDataLayer('view_item_list', { item_list_id: collection.handle, items_count: collection.productsCount });
});

document.addEventListener(StandardEvents.collectionUpdate, async (event) => {
  const { collection, productFilters, sortKey, promise } = event;
  const result = await promise.catch(() => null);
  dispatchToDataLayer('filter_item_list', {
    item_list_id: collection.handle,
    filters: productFilters ? JSON.stringify(productFilters) : undefined,
    sort_key: sortKey,
    result_count: result?.productsCount,
  });
});

document.addEventListener(StandardEvents.searchUpdate, (event) => {
  const { search } = event;
  dispatchToDataLayer('search', { search_term: search.query });
});

document.addEventListener(StandardEvents.cartView, (event) => {
  const { cart } = event;
  (cart?.lines ?? []).forEach((line) => cartLineQuantities.set(line.id, line.quantity));
  dispatch('view_cart', { value: cart?.cost?.totalAmount?.amount, currency: cart?.cost?.totalAmount?.currencyCode });
});

document.addEventListener(StandardEvents.cartLinesUpdate, async (event) => {
  const { action, lines, promise } = event;
  const result = await promise.catch(() => null);
  if (!result?.cart) return; // failed update — shopify:cart:error covers the failure signal

  if (action === 'add') {
    dispatch('add_to_cart', { items: lines.map((l) => ({ item_id: l.merchandiseId, quantity: l.quantity })) });
  } else if (action === 'remove') {
    dispatch('remove_from_cart', { items: lines.map((l) => ({ cart_line_id: l.id, quantity: l.quantity })) });
    lines.forEach((l) => cartLineQuantities.delete(l.id));
  } else if (action === 'update') {
    // Payload only carries the resulting target quantity per line, not a delta — diff
    // against the last known quantity for that line to decide add vs remove. If this
    // line hasn't been seen yet (e.g. page loaded straight into an update with no prior
    // cartView/add/remove in this session), there's no baseline to diff against, so this
    // one change is intentionally not reported rather than guessing a direction.
    lines.forEach((l) => {
      const prevQty = cartLineQuantities.get(l.id);
      if (prevQty != null) {
        const delta = l.quantity - prevQty;
        if (delta > 0) dispatch('add_to_cart', { items: [{ cart_line_id: l.id, quantity: delta }] });
        else if (delta < 0) dispatch('remove_from_cart', { items: [{ cart_line_id: l.id, quantity: -delta }] });
      }
    });
  }

  // Re-seed from the authoritative post-update cart state so the next diff is correct
  // even if a line changed indirectly (e.g. a promotion adjusting an unrelated line).
  result.cart.lines.forEach((l) => cartLineQuantities.set(l.id, l.quantity));
});

document.addEventListener(StandardEvents.cartDiscountUpdate, (event) => {
  const { discountCodes } = event;
  dispatch('select_content', {
    content_type: 'cart_discount',
    content_id: discountCodes?.length ? discountCodes.map((d) => d.code).join(',') : 'remove',
  });
});

document.addEventListener(StandardEvents.cartError, (event) => {
  // Diagnostic only — not part of the GA4 event dictionary. Supports the "click intent
  // vs actual result" comparison in tracking-plan-master-v1.md's M-1.
  dispatch('cart_error', { error_code: event.code, error_message: event.error });
});
